import { useRouter } from 'common/hooks/use-router';
import { useIntersectionObserverCallback } from '@navi-toolbox/hooks';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { analyticsUtils } from 'utils/analytics-utils';
import { getAnalyticsData } from './utils';
import type { UseProductLogicProps } from './product.types';
import type { ProductPriceCategory, SponsoredProductType } from 'features/products/types';
import { useBasketUpdate } from 'common/hooks/use-basket-update';
import { getGaTag, getProductBelongFromSource, isOnlineOnly } from 'common/utils/analytics';

import { useTracking } from 'features/tracking/use-tracking';
import { GA_ADD_TO_SHOPPING_LIST, GA_REMOVE_FROM_SHOPPING_LIST } from '../constants';

export const useProductLogic = ({
  elementBlock = 'not set',
  product,
  qty = 0,
  productPosition,
  addToBasket,
  removeFromBasket,
  loadBasket,
  updateBasket,
  ratings,
  updateCartItem,
  addToShoppingList,
  removeFromShoppingList,
}: UseProductLogicProps) => {
  const contentId = (product as SponsoredProductType)?.contentId;
  const isMonetization = (product as SponsoredProductType)?.monetization ?? null;

  const productRef = useRef<HTMLDivElement | null>(null);
  const prevQtyRef = useRef<number>(qty);
  const router = useRouter();
  const path = router.getPath();

  const { sendClickEvent } = useTracking(contentId, productRef);

  useBasketUpdate(loadBasket, updateBasket);

  useEffect(() => {
    prevQtyRef.current = qty;
  }, [qty]);

  const label: ProductPriceCategory | 'PIM' = useMemo(
    () => (isOnlineOnly(product.source) ? 'PIM' : product.productPriceCategory),
    [product.productPriceCategory, product.source],
  );

  const productBelong = useMemo(() => {
    return getProductBelongFromSource(product.source);
  }, [product.source]);

  const GAdata = useMemo(() => {
    const analytics = {
      productFamilyId: product.compareCategory?.familyId,
      division: product.mgtNomencProduct_mgtNomenc_departmentID,
      subdivision: product.mgtNomencProduct_mgtNomenc_subDepartmentID,
    };
    return getAnalyticsData(elementBlock, product, analytics);
  }, [elementBlock, product]);

  const gaTag = useMemo(() => {
    return getGaTag(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.productPriceCategory, product.price.previous_price, product.source]);

  const review = useMemo(
    () => ratings?.find((item) => item.productId?.toString() === product.productId) ?? null,
    [product.productId, ratings],
  );

  const commonGaData = useMemo(
    () => ({
      elementBlock,
      elementType: 'product',
      productName: product.displayedName,
      productId: product.productId,
      productPrice: product.price.main_price,
      productPosition,
      productPrevPrice: product.price.previous_price ?? null,
      productDiscount: product.price.discount_percent ?? null,
      isMonetization,
      productBelong: productBelong,
      productTag: gaTag,
      ...GAdata,
    }),
    [elementBlock, product, productPosition, gaTag, GAdata, productBelong, isMonetization],
  );

  const handleAddToCart = useCallback(() => {
    const step = product.price.step || 1;
    let initialSteps = Math.round(1 / step);

    if (step * initialSteps !== 1) initialSteps += 1;
    if (step > 1) initialSteps = 1;

    addToBasket(product.productId, step * initialSteps);
    prevQtyRef.current = step * initialSteps;

    sendClickEvent();

    analyticsUtils.sendEvent('GA:addToCart', {
      productQuantity: parseFloat((step * initialSteps).toFixed(2)).toString(),
      ...commonGaData,
      elementType: path === '/' ? 'product' : 'button',
    });
  }, [addToBasket, sendClickEvent, commonGaData, product, path]);

  const handleUpdateCart = useCallback(
    (code: string, quantity: number) => {
      updateCartItem(code, quantity);

      if (quantity) {
        sendClickEvent();
      }

      analyticsUtils.sendEvent(
        quantity >= (prevQtyRef?.current || 0) ? 'GA:addToCart' : 'GA:removeFromCart',
        {
          productQuantity: parseFloat(Math.abs(quantity - prevQtyRef.current).toFixed(2)).toString(),
          ...commonGaData,
          elementType: path === '/' ? 'product' : 'button',
        },
      );

      prevQtyRef.current = quantity;
    },
    [commonGaData, sendClickEvent, updateCartItem, path],
  );

  const handleRemoveFromCart = useCallback(() => {
    removeFromBasket(product.productId);
  }, [product, removeFromBasket]);

  useIntersectionObserverCallback(
    productRef,
    {
      enter: () => {
        analyticsUtils.sendEvent('GA:impressions', {
          ...commonGaData,
        });
      },
      extraUnobserve: 'enter',
      options: { rootMargin: '-30px' },
    },
    [productPosition],
  );

  const toggleShoppingList = useCallback(
    (nextInShoppingList: boolean) => {
      if (nextInShoppingList) {
        addToShoppingList(product.productId);
      } else {
        removeFromShoppingList(product.productId);
      }

      sendClickEvent();

      analyticsUtils.sendEvent(nextInShoppingList ? GA_ADD_TO_SHOPPING_LIST : GA_REMOVE_FROM_SHOPPING_LIST, {
        ...commonGaData,
        elementType: 'button',
      });
    },
    [addToShoppingList, removeFromShoppingList, sendClickEvent, commonGaData, product.productId],
  );

  const onClick = useCallback(
    (elementName: 'productNameLink' | 'productPhotoLink' | 'productRatingLink') => {
      sendClickEvent();

      analyticsUtils.sendEvent('GA:clickProduct', {
        elementName,
        ...commonGaData,
      });
    },
    [commonGaData, sendClickEvent],
  );

  return {
    productRef,
    label,
    review,
    handleAddToCart,
    handleUpdateCart,
    handleRemoveFromCart,
    toggleShoppingList,
    onClick,
  };
};
